import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, Loading, Select, ImageUpload } from '../components/ui';
import { useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface Release {
  id: number;
  title: string;
  type: string;
  cover_url?: string;
  spotify_url?: string;
  youtube_url?: string;
  release_date?: string;
}

const releaseTypes = [
  { value: 'single', label: 'Single' },
  { value: 'ep', label: 'EP' },
  { value: 'album', label: 'Album' },
  { value: 'mixtape', label: 'Mixtape' },
];

const ReleasesPage: React.FC = () => {
  const releases = useCrud<Release>('/api/releases');
  const { showToast } = useToast();
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);

  useEffect(() => { releases.fetchAll(); }, []);

  const handleSave = async (release: Partial<Release>) => {
    if (editingRelease?.id) {
      const result = await releases.update(editingRelease.id, release);
      if (result) {
        showToast('Release updated!', 'success');
        setEditingRelease(null);
      }
    } else {
      const result = await releases.create(release);
      if (result) {
        showToast('Release added!', 'success');
        setEditingRelease(null);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this release?')) {
      const result = await releases.remove(id);
      if (result) showToast('Release deleted', 'success');
    }
  };

  if (releases.loading && releases.items.length === 0) return <Loading />;

  return (
    <>
      <PageHeader
        title="Releases"
        subtitle="Manage your singles, EPs, and albums"
        actions={
          <Button onClick={() => setEditingRelease({ id: 0, title: '', type: 'single' })}>
            + Add Release
          </Button>
        }
      />

      <div className="admin-content">
        <Card>
          <div className="admin-list-editor">
            {releases.items.map(release => (
              <div key={release.id} className="admin-list-item">
                {release.cover_url ? (
                  <img src={release.cover_url} alt={release.title} style={{ width: 48, height: 48, borderRadius: 4, objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '1.5rem' }}>💿</span>
                )}
                <div className="admin-list-item-content">
                  <div>
                    <strong>{release.title}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                      {release.type} {release.release_date && `• ${new Date(release.release_date).toLocaleDateString()}`}
                    </div>
                  </div>
                </div>
                <div className="admin-list-item-actions">
                  <Button size="sm" variant="secondary" onClick={() => setEditingRelease(release)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(release.id)}>Delete</Button>
                </div>
              </div>
            ))}
            {releases.items.length === 0 && (
              <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 20 }}>
                No releases yet. Add your first release above.
              </p>
            )}
          </div>
        </Card>

        {editingRelease && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setEditingRelease(null)}>
            <div style={{ background: 'var(--admin-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 20 }}>{editingRelease.id ? 'Edit Release' : 'Add Release'}</h3>
              <ReleaseForm
                release={editingRelease}
                onSave={handleSave}
                onCancel={() => setEditingRelease(null)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const ReleaseForm: React.FC<{
  release: Release;
  onSave: (r: Partial<Release>) => void;
  onCancel: () => void;
}> = ({ release, onSave, onCancel }) => {
  const [form, setForm] = useState(release);

  return (
    <div className="admin-form">
      <Input label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
      <Select label="Type" options={releaseTypes} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} />
      <ImageUpload label="Cover Art" value={form.cover_url} onChange={url => setForm(f => ({ ...f, cover_url: url || undefined }))} />
      <Input label="Release Date" type="date" value={form.release_date || ''} onChange={e => setForm(f => ({ ...f, release_date: e.target.value }))} />
      <Input label="Spotify URL" value={form.spotify_url || ''} onChange={e => setForm(f => ({ ...f, spotify_url: e.target.value }))} placeholder="https://open.spotify.com/..." />
      <Input label="YouTube URL" value={form.youtube_url || ''} onChange={e => setForm(f => ({ ...f, youtube_url: e.target.value }))} placeholder="https://youtube.com/..." />

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button onClick={() => onSave(form)}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default ReleasesPage;
